// BotDetail.jsx — Phase 2: Bot Detail Page / Phase 4: Quality Gating
// Tabs: Overview | Training | Persona | Test | Shadow Review (owner-only)

import React, { useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Bot, Mic2, MessageSquare, BookOpen, UploadCloud, Trash2,
  CheckCircle2, AlertCircle, Clock, Loader2, Sparkles, Play, RefreshCw,
  ChevronDown, Lock, Users, GitFork, Globe, Brain, Zap, FileText,
  Volume2, Save, Plus, Minus, X, MessageCircle, Search, Share2,
  UserCheck, Shield, UserMinus, Eye, Star, ShieldAlert, ThumbsDown,
  ThumbsUp, AlertTriangle, CheckCheck,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { apiGet, apiPost, apiPut, apiDel } from '../lib/api.js';
import TopBar from '../components/TopBar.jsx';

// ─── Shared helpers ───────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  draft:        { label: 'Draft',       color: 'text-white/50',   bg: 'bg-white/5'        },
  training:     { label: 'Training…',   color: 'text-yellow-400', bg: 'bg-yellow-400/10'  },
  ready:        { label: 'Ready',       color: 'text-civix-cyan', bg: 'bg-cyan-500/10'    },
  archived:     { label: 'Archived',    color: 'text-white/30',   bg: 'bg-white/5'        },
  queued:       { label: 'Queued',      color: 'text-white/50',   bg: 'bg-white/5'        },
  transcribing: { label: 'Transcribing',color: 'text-yellow-400', bg: 'bg-yellow-400/10'  },
  analyzing:    { label: 'Analyzing',   color: 'text-blue-400',   bg: 'bg-blue-400/10'    },
  failed:       { label: 'Failed',      color: 'text-red-400',    bg: 'bg-red-400/10'     },
};

const SHARE_OPTIONS = [
  { value: 'private',           label: 'Private',     icon: Lock    },
  { value: 'downline_locked',   label: 'Downline',    icon: Users   },
  { value: 'downline_forkable', label: 'Forkable',    icon: GitFork },
  { value: 'marketplace',       label: 'Marketplace', icon: Globe   },
];

// Phase 4: Quality badge for AI judge decision
const JUDGE_BADGE = {
  pass:   { label: 'Pass',   color: 'text-green-400',  bg: 'bg-green-400/10',  icon: CheckCircle2  },
  review: { label: 'Review', color: 'text-yellow-400', bg: 'bg-yellow-400/10', icon: Eye           },
  reject: { label: 'Reject', color: 'text-red-400',    bg: 'bg-red-400/10',    icon: ShieldAlert   },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

// Phase 4: Quality badge for AI judge decision on a TrainingCall
function JudgeBadge({ decision }) {
  if (!decision) return null;
  const cfg = JUDGE_BADGE[decision] || JUDGE_BADGE.review;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.color}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

// ─── ShareModal ───────────────────────────────────────────────────────────────
function ShareModal({ bot, onClose }) {
  const qc = useQueryClient();
  const [revoking, setRevoking] = useState(null);

  const { data: sharesData, isLoading, refetch: refetchShares } = useQuery({
    queryKey: ['bot-shares', bot._id],
    queryFn:  () => apiGet(`/bots/share/bot/${bot._id}/shares`),
    staleTime: 5000,
  });
  const shares = sharesData?.data || [];

  const handleRevoke = async (tenantId, tenantName) => {
    if (!window.confirm(`Revoke access for ${tenantName}?`)) return;
    setRevoking(tenantId);
    try {
      await apiPost(`/bots/share/${bot._id}/revoke/${tenantId}`, {});
      toast.success(`Access revoked for ${tenantName}`);
      refetchShares();
      qc.invalidateQueries({ queryKey: ['bots'] });
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to revoke');
    } finally {
      setRevoking(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4">
      <div className="panel w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-civix-purple/20 flex items-center justify-center">
              <Share2 className="w-5 h-5 text-civix-purple" />
            </div>
            <div>
              <div className="font-semibold text-white">Share Bot</div>
              <div className="text-xs text-white/40">{bot.name}</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5 text-white/40 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-5">
          <div className="text-xs text-white/50 uppercase tracking-wider mb-3">Active Shares</div>
          {isLoading ? (
            <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 text-civix-purple animate-spin" /></div>
          ) : shares.length === 0 ? (
            <div className="text-sm text-white/30 text-center py-4 bg-white/3 rounded-lg">
              No active shares — approve requests from Bot Sync Requests to grant access.
            </div>
          ) : (
            <div className="space-y-2">
              {shares.map((s) => (
                <div key={String(s.tenantId)} className="flex items-center justify-between gap-3 bg-white/3 rounded-lg px-3 py-2.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <UserCheck className="w-4 h-4 text-civix-cyan shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm text-white truncate">{s.tenantName}</div>
                      <div className="text-xs text-white/30 flex items-center gap-1">
                        {s.scope === 'clone' ? <GitFork className="w-3 h-3" /> : <Shield className="w-3 h-3" />}
                        {s.scope === 'clone' ? 'Use + Clone' : 'Use only'}
                        {s.grantedAt && ` · since ${new Date(s.grantedAt).toLocaleDateString()}`}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRevoke(String(s.tenantId), s.tenantName)}
                    disabled={revoking === String(s.tenantId)}
                    className="p-1.5 rounded-lg text-white/20 hover:text-red-400 hover:bg-red-400/10 transition-colors shrink-0"
                    title="Revoke access"
                  >
                    {revoking === String(s.tenantId)
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <UserMinus className="w-3.5 h-3.5" />}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-civix-purple/10 border border-civix-purple/20 rounded-lg p-4 text-xs text-white/60">
          <div className="flex items-start gap-2">
            <Shield className="w-3.5 h-3.5 text-civix-purple shrink-0 mt-0.5" />
            <div>
              <span className="font-medium text-white/80">Access is request-based.</span>{' '}
              Agents or agencies submit a sync request. Approve or deny each individually from{' '}
              <span className="text-civix-cyan">AI Tools → Bot Sync Requests</span>.
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-5">
          <button onClick={onClose} className="btn-secondary">Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Overview ────────────────────────────────────────────────────────────
function OverviewTab({ bot, refetch, isOwner }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name:         bot.name        || '',
    description:  bot.description || '',
    shareability: bot.shareability || 'private',
    script: {
      intro:              bot.script?.intro              || '',
      qualifyingQuestions: bot.script?.qualifyingQuestions || [],
      closingPitch:        bot.script?.closingPitch       || '',
      calendarLink:        bot.script?.calendarLink       || '',
    },
  });
  const [saving, setSaving] = useState(false);

  const { data: voicesData } = useQuery({
    queryKey: ['agent-voices'],
    queryFn:  () => apiGet('/ai-dialer/voices'),
    staleTime: 60000,
  });
  const voices = voicesData?.data || voicesData || [];

  const handleSave = async () => {
    if (!isOwner) return toast.error('You do not own this bot — read only');
    setSaving(true);
    try {
      await apiPut(`/bots/${bot._id}`, form);
      toast.success('Bot saved');
      refetch();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const addQuestion = () =>
    setForm((f) => ({ ...f, script: { ...f.script, qualifyingQuestions: [...f.script.qualifyingQuestions, ''] } }));
  const removeQuestion = (i) =>
    setForm((f) => ({ ...f, script: { ...f.script, qualifyingQuestions: f.script.qualifyingQuestions.filter((_, idx) => idx !== i) } }));
  const updateQuestion = (i, v) =>
    setForm((f) => {
      const qs = [...f.script.qualifyingQuestions];
      qs[i] = v;
      return { ...f, script: { ...f.script, qualifyingQuestions: qs } };
    });

  return (
    <div className="space-y-6">
      {/* Basic info */}
      <div className="panel p-5">
        <div className="text-sm font-medium text-white/60 mb-4 uppercase tracking-wider">Basic Info</div>
        <div className="space-y-4">
          <div>
            <label className="block text-xs text-white/50 mb-1.5">Bot Name</label>
            <input className="input w-full" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1.5">Description</label>
            <input className="input w-full" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Optional description" />
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1.5">Shareability</label>
            <select
              className="input w-full"
              value={form.shareability}
              onChange={(e) => setForm((f) => ({ ...f, shareability: e.target.value }))}
            >
              {SHARE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Script */}
      <div className="panel p-5">
        <div className="text-sm font-medium text-white/60 mb-4 uppercase tracking-wider">Script</div>
        <div className="space-y-4">
          <div>
            <label className="block text-xs text-white/50 mb-1.5">Intro</label>
            <textarea
              className="input w-full resize-none"
              rows={3}
              placeholder="Hi, this is {{agentName}} calling from {{company}}…"
              value={form.script.intro}
              onChange={(e) => setForm((f) => ({ ...f, script: { ...f.script, intro: e.target.value } }))}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs text-white/50">Qualifying Questions</label>
              <button onClick={addQuestion} className="text-xs text-civix-purple hover:text-civix-purple/80 flex items-center gap-1">
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
            <div className="space-y-2">
              {form.script.qualifyingQuestions.map((q, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    className="input flex-1 text-sm"
                    value={q}
                    placeholder={`Question ${i + 1}`}
                    onChange={(e) => updateQuestion(i, e.target.value)}
                  />
                  <button onClick={() => removeQuestion(i)} className="p-2 text-white/30 hover:text-red-400 transition-colors">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs text-white/50 mb-1.5">Closing Pitch</label>
            <textarea
              className="input w-full resize-none"
              rows={2}
              placeholder="I'd love to set up a quick 15-minute call…"
              value={form.script.closingPitch}
              onChange={(e) => setForm((f) => ({ ...f, script: { ...f.script, closingPitch: e.target.value } }))}
            />
          </div>

          <div>
            <label className="block text-xs text-white/50 mb-1.5">Calendar Link</label>
            <input
              className="input w-full"
              placeholder="https://calendly.com/…"
              value={form.script.calendarLink}
              onChange={(e) => setForm((f) => ({ ...f, script: { ...f.script, calendarLink: e.target.value } }))}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save
        </button>
      </div>
    </div>
  );
}

// ─── Tab: Training ────────────────────────────────────────────────────────────
function TrainingTab({ bot, refetch: refetchBot }) {
  const qc = useQueryClient();
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [showPaste, setShowPaste] = useState(false);
  const [derivingPersona, setDerivingPersona] = useState(false);
  const [decidingId, setDecidingId] = useState(null);
  const fileInput = useRef(null);

  const { data: callsData, refetch: refetchCalls, isLoading } = useQuery({
    queryKey: ['bot-training-calls', bot._id],
    queryFn:  () => apiGet(`/bots/${bot._id}/training`),
    staleTime: 5000,
    refetchInterval: (data) => {
      const calls = data?.data || [];
      const hasProcessing = calls.some((c) => ['queued', 'transcribing', 'analyzing'].includes(c.status));
      return hasProcessing ? 3000 : 15000;
    },
  });
  const calls = callsData?.data || [];

  const uploadAudio = async (file) => {
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/bots/${bot._id}/training/audio`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('civix_token')}` },
        body:    fd,
      });
      if (!r.ok) throw new Error((await r.json()).error || 'Upload failed');
      toast.success('Upload started — processing in background');
      refetchCalls();
      refetchBot();
    } catch (e) {
      toast.error(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) uploadAudio(file);
  }, [bot._id]);

  const uploadTranscript = async () => {
    if (!pasteText.trim()) return toast.error('Transcript is empty');
    try {
      await apiPost(`/bots/${bot._id}/training/transcript`, { transcriptText: pasteText });
      toast.success('Transcript submitted');
      setPasteText('');
      setShowPaste(false);
      refetchCalls();
      refetchBot();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed');
    }
  };

  const deleteCall = async (callId) => {
    try {
      await apiDel(`/bots/${bot._id}/training/${callId}`);
      toast.success('Training call removed');
      refetchCalls();
      refetchBot();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed');
    }
  };

  const derivePersona = async () => {
    setDerivingPersona(true);
    try {
      await apiPost(`/bots/${bot._id}/derive-persona`, {});
      toast.success('Persona derivation started — check the Persona tab in a moment');
      refetchBot();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed');
    } finally {
      setDerivingPersona(false);
    }
  };

  // Phase 4: Mark as Reference / Anti-corpus from Training tab
  const applyDecision = async (callId, decision) => {
    setDecidingId(callId);
    try {
      await apiPost(`/bots/${bot._id}/training/${callId}/decision`, { decision });
      const labels = { reference: 'Promoted to Reference', 'anti-corpus': 'Added to Anti-Corpus', approved: 'Approved', rejected: 'Rejected' };
      toast.success(labels[decision] || 'Updated');
      refetchCalls();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed');
    } finally {
      setDecidingId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Calls', value: bot.trainingCorpusSize || 0 },
          { label: 'Minutes', value: Math.round(bot.trainingCorpusMinutes || 0) },
          { label: 'Status', value: bot.status || 'draft' },
        ].map((s) => (
          <div key={s.label} className="panel p-4 text-center">
            <div className="text-2xl font-bold text-white">{s.value}</div>
            <div className="text-xs text-white/40 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Upload zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInput.current?.click()}
        className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
          dragging ? 'border-civix-purple bg-civix-purple/10' : 'border-white/10 hover:border-civix-purple/50 hover:bg-white/5'
        }`}
      >
        <input
          ref={fileInput}
          type="file"
          accept="audio/*,video/*"
          className="hidden"
          onChange={(e) => e.target.files?.[0] && uploadAudio(e.target.files[0])}
        />
        {uploading ? (
          <Loader2 className="w-8 h-8 text-civix-purple animate-spin mx-auto mb-2" />
        ) : (
          <UploadCloud className="w-8 h-8 text-white/30 mx-auto mb-2" />
        )}
        <div className="text-sm text-white/60 font-medium">{uploading ? 'Uploading…' : 'Drag & drop audio or video'}</div>
        <div className="text-xs text-white/30 mt-1">MP3, WAV, MP4, M4A, WebM — up to 500MB</div>
      </div>

      {/* Paste transcript */}
      <div className="panel p-4">
        <button
          onClick={() => setShowPaste(!showPaste)}
          className="flex items-center gap-2 text-sm text-white/60 hover:text-white w-full"
        >
          <FileText className="w-4 h-4" />
          Paste Transcript
          <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${showPaste ? 'rotate-180' : ''}`} />
        </button>
        {showPaste && (
          <div className="mt-4 space-y-3">
            <textarea
              className="input w-full resize-none text-sm"
              rows={6}
              placeholder={"AGENT: Hi, I'm calling about your insurance…\nPROSPECT: Who is this?\nAGENT: I'm reaching out from…"}
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setShowPaste(false); setPasteText(''); }} className="btn-secondary text-sm px-4 py-1.5">Cancel</button>
              <button onClick={uploadTranscript} className="btn-primary text-sm px-4 py-1.5 flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" />
                Submit
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Training calls list */}
      {isLoading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-civix-purple animate-spin" /></div>
      ) : calls.length === 0 ? (
        <div className="text-center py-12 text-white/30 text-sm">No training calls yet — upload audio or paste a transcript above</div>
      ) : (
        <div className="space-y-2">
          {calls.map((tc) => (
            <div key={tc._id} className="panel p-4">
              <div className="flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-sm font-medium text-white truncate">{tc.originalFilename || `Transcript #${tc._id.slice(-6)}`}</span>
                    <StatusBadge status={tc.status} />
                    {/* Phase 4: AI judge quality badge */}
                    {tc.source === 'shadow_mode' && tc.decision && (
                      <JudgeBadge decision={tc.decision} />
                    )}
                    {/* Phase 4: reference call star */}
                    {bot.referenceCalls?.some((id) => String(id) === String(tc._id)) && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-400/10 text-yellow-400">
                        <Star className="w-3 h-3" /> Reference
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-white/30 flex gap-3 flex-wrap">
                    <span>{tc.source?.replace('_', ' ')}</span>
                    {tc.durationSeconds > 0 && <span>{Math.round(tc.durationSeconds / 60)}m</span>}
                    {tc.qualityScore > 0 && <span>Quality: {Math.round(tc.qualityScore * 100)}%</span>}
                    {tc.judgeBreakdown?.overallScore != null && (
                      <span>Judge: {tc.judgeBreakdown.overallScore}/100</span>
                    )}
                    <span>{new Date(tc.createdAt).toLocaleDateString()}</span>
                  </div>
                  {tc.callOutcome && (
                    <div className="text-xs text-civix-cyan mt-1">Outcome: {tc.callOutcome}</div>
                  )}
                  {/* Compliance flags warning */}
                  {tc.judgeBreakdown?.complianceFlags?.length > 0 && (
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <ShieldAlert className="w-3 h-3 text-red-400 shrink-0" />
                      <span className="text-xs text-red-400">
                        {tc.judgeBreakdown.complianceFlags.join(' · ')}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {/* Phase 4: Mark as Reference button (shadow calls only) */}
                  {tc.source === 'shadow_mode' && tc.status === 'ready' && (
                    <>
                      <button
                        onClick={() => applyDecision(tc._id, 'reference')}
                        disabled={decidingId === tc._id}
                        title="Promote to Reference"
                        className="p-1.5 rounded-lg text-white/20 hover:text-yellow-400 hover:bg-yellow-400/10 transition-colors"
                      >
                        {decidingId === tc._id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Star className="w-3.5 h-3.5" />}
                      </button>
                      <button
                        onClick={() => applyDecision(tc._id, 'anti-corpus')}
                        disabled={decidingId === tc._id}
                        title="Add to Anti-Corpus"
                        className="p-1.5 rounded-lg text-white/20 hover:text-red-400 hover:bg-red-400/10 transition-colors"
                      >
                        <ThumbsDown className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => deleteCall(tc._id)}
                    className="p-1.5 text-white/20 hover:text-red-400 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Derive persona CTA */}
      {calls.filter((c) => c.status === 'ready').length > 0 && (
        <div className="panel p-5 border border-civix-purple/30 flex items-center justify-between gap-4">
          <div>
            <div className="font-semibold text-white mb-0.5">Ready to derive persona?</div>
            <div className="text-xs text-white/40">
              {calls.filter((c) => c.status === 'ready').length} call{calls.filter((c) => c.status === 'ready').length !== 1 ? 's' : ''} ready.
              GPT-4o will analyze the corpus and generate your AgentDNA.
            </div>
          </div>
          <button
            onClick={derivePersona}
            disabled={derivingPersona}
            className="btn-primary shrink-0 flex items-center gap-2"
          >
            {derivingPersona ? <Loader2 className="w-4 h-4 animate-spin" /> : <Brain className="w-4 h-4" />}
            {derivingPersona ? 'Deriving…' : 'Re-derive Persona'}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Persona ─────────────────────────────────────────────────────────────
function PersonaTab({ bot, refetch }) {
  const p = bot.personaProfile || {};
  const hasPersona = p.toneDescriptors?.length > 0 || p.signatureOpeners?.length > 0;
  const [derivingPersona, setDerivingPersona] = useState(false);

  const derivePersona = async () => {
    setDerivingPersona(true);
    try {
      await apiPost(`/bots/${bot._id}/derive-persona`, {});
      toast.success('Persona derivation started — refresh in 30 seconds');
      refetch();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed');
    } finally {
      setDerivingPersona(false);
    }
  };

  if (!hasPersona) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <Brain className="w-12 h-12 text-white/20 mb-4" />
        <div className="text-white font-semibold mb-1">No Persona Yet</div>
        <div className="text-white/40 text-sm mb-6 max-w-xs">
          Upload training calls and click "Re-derive Persona" in the Training tab to generate AgentDNA.
        </div>
        <button
          onClick={derivePersona}
          disabled={derivingPersona}
          className="btn-primary flex items-center gap-2"
        >
          {derivingPersona ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          Derive Persona Now
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Meta */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-white/30">
          Version {bot.version || 1}
          {bot.lastTrainedAt && ` · Trained ${new Date(bot.lastTrainedAt).toLocaleDateString()}`}
          {p.derivedFrom?.trainingCallIds?.length > 0 && ` · ${p.derivedFrom.trainingCallIds.length} calls`}
        </div>
        <button onClick={derivePersona} disabled={derivingPersona} className="btn-secondary text-sm flex items-center gap-1.5">
          {derivingPersona ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Re-derive
        </button>
      </div>

      {/* Tone descriptors */}
      {p.toneDescriptors?.length > 0 && (
        <div className="panel p-5">
          <div className="text-xs text-white/50 uppercase tracking-wider mb-3">Tone</div>
          <div className="flex flex-wrap gap-2">
            {p.toneDescriptors.map((t) => (
              <span key={t} className="px-3 py-1 rounded-full bg-civix-purple/20 text-civix-purple text-sm">{t}</span>
            ))}
          </div>
        </div>
      )}

      {/* Signature openers */}
      {p.signatureOpeners?.length > 0 && (
        <div className="panel p-5">
          <div className="text-xs text-white/50 uppercase tracking-wider mb-3">Signature Openers</div>
          <div className="space-y-2">
            {p.signatureOpeners.map((o, i) => (
              <div key={i} className="text-sm text-white/70 pl-3 border-l-2 border-civix-purple/40">"{o}"</div>
            ))}
          </div>
        </div>
      )}

      {/* Objection playbook */}
      {p.objectionPlaybook?.length > 0 && (
        <div className="panel p-5">
          <div className="text-xs text-white/50 uppercase tracking-wider mb-3">Objection Playbook</div>
          <div className="space-y-3">
            {p.objectionPlaybook.map((entry, i) => (
              <div key={i} className="bg-white/3 rounded-lg p-3">
                <div className="text-xs text-red-400 mb-1.5">
                  "{entry.objection}"
                </div>
                <div className="text-sm text-white/70">{entry.rebuttal}</div>
                <div className="text-xs text-white/20 mt-1">{entry.source}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Closing patterns */}
      {p.closingPatterns?.length > 0 && (
        <div className="panel p-5">
          <div className="text-xs text-white/50 uppercase tracking-wider mb-3">Closing Patterns</div>
          <div className="space-y-2">
            {p.closingPatterns.map((c, i) => (
              <div key={i} className="text-sm text-civix-cyan pl-3 border-l-2 border-civix-cyan/40">"{c}"</div>
            ))}
          </div>
        </div>
      )}

      {/* Pacing */}
      {p.pacingRules && (
        <div className="panel p-5">
          <div className="text-xs text-white/50 uppercase tracking-wider mb-2">Pacing Rules</div>
          <div className="text-sm text-white/70">{p.pacingRules}</div>
        </div>
      )}

      {/* Do / Don't Say */}
      <div className="grid grid-cols-2 gap-4">
        {p.doSayList?.length > 0 && (
          <div className="panel p-4">
            <div className="text-xs text-white/50 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <CheckCircle2 className="w-3 h-3 text-green-400" /> Do Say
            </div>
            <ul className="space-y-1.5">
              {p.doSayList.map((s, i) => <li key={i} className="text-xs text-white/70">• {s}</li>)}
            </ul>
          </div>
        )}
        {p.dontSayList?.length > 0 && (
          <div className="panel p-4">
            <div className="text-xs text-white/50 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <X className="w-3 h-3 text-red-400" /> Don't Say
            </div>
            <ul className="space-y-1.5">
              {p.dontSayList.map((s, i) => <li key={i} className="text-xs text-white/70">• {s}</li>)}
            </ul>
          </div>
        )}
      </div>

      {/* Sample quotes */}
      {p.sampleQuotes?.length > 0 && (
        <div className="panel p-5">
          <div className="text-xs text-white/50 uppercase tracking-wider mb-3">Notable Quotes</div>
          <div className="space-y-3">
            {p.sampleQuotes.map((q, i) => (
              <div key={i} className="bg-white/3 rounded-lg p-3">
                <div className="text-xs text-white/40 mb-1">{q.context}</div>
                <div className="text-sm text-white italic">"{q.quote}"</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Test ────────────────────────────────────────────────────────────────
function TestTab({ bot }) {
  const [scenario, setScenario] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const runTest = async () => {
    if (!scenario.trim()) return toast.error('Enter a scenario');
    setLoading(true);
    setResult(null);
    try {
      const r = await apiPost(`/bots/${bot._id}/test`, { scenario });
      setResult(r?.data || r);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Test failed');
    } finally {
      setLoading(false);
    }
  };

  const EXAMPLE_SCENARIOS = [
    "I'm not interested",
    "I already have coverage",
    "How much does this cost?",
    "I'm too busy right now",
    "Send me some information instead",
  ];

  return (
    <div className="space-y-6">
      {/* Input */}
      <div className="panel p-5">
        <div className="text-xs text-white/50 uppercase tracking-wider mb-3">Customer Situation</div>
        <textarea
          className="input w-full resize-none text-sm"
          rows={3}
          placeholder="Customer says: I already have insurance, I don't need anything."
          value={scenario}
          onChange={(e) => setScenario(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), runTest())}
        />

        {/* Quick scenarios */}
        <div className="mt-3 flex flex-wrap gap-2">
          {EXAMPLE_SCENARIOS.map((s) => (
            <button
              key={s}
              onClick={() => setScenario(s)}
              className="text-xs px-2.5 py-1 rounded-full bg-white/5 text-white/50 hover:bg-white/10 hover:text-white transition-colors"
            >
              {s}
            </button>
          ))}
        </div>

        <div className="flex justify-end mt-4">
          <button
            onClick={runTest}
            disabled={loading}
            className="btn-primary flex items-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            Test Bot
          </button>
        </div>
      </div>

      {/* Result */}
      {result && (
        <div className="space-y-4">
          {/* Bot response */}
          <div className="panel p-5 border border-civix-cyan/20">
            <div className="flex items-center gap-2 mb-3">
              <Bot className="w-4 h-4 text-civix-cyan" />
              <span className="text-xs text-civix-cyan uppercase tracking-wider font-medium">Bot Response</span>
              {result.usage?.costUsd > 0 && (
                <span className="ml-auto text-xs text-white/20">${result.usage.costUsd.toFixed(4)}</span>
              )}
            </div>
            <div className="text-white text-sm leading-relaxed">
              "{result.botResponse}"
            </div>
          </div>

          {/* RAG context */}
          {result.ragResults?.length > 0 && (
            <div className="panel p-5">
              <div className="flex items-center gap-2 mb-3">
                <Search className="w-4 h-4 text-civix-purple" />
                <span className="text-xs text-civix-purple uppercase tracking-wider font-medium">
                  Training Examples Retrieved ({result.ragResults.length})
                </span>
              </div>
              <div className="space-y-3">
                {result.ragResults.map((r, i) => (
                  <div key={i} className="bg-white/3 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs text-white/30">Match {i + 1}</span>
                      <span className="text-xs text-civix-cyan">{(r.score * 100).toFixed(0)}% match</span>
                    </div>
                    <div className="text-xs text-white/60 line-clamp-3">{r.chunk}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Tab: Shadow Review ───────────────────────────────────────────────────────
// Phase 4 Quality Gating: review queue for shadow-mode calls that scored 40–70
function ShadowReviewTab({ bot, refetch: refetchBot }) {
  const qc = useQueryClient();
  const [decidingId, setDecidingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const { data, refetch, isLoading } = useQuery({
    queryKey: ['bot-review-queue', bot._id],
    queryFn:  () => apiGet(`/bots/${bot._id}/review-queue`),
    staleTime: 10000,
    refetchInterval: 30000,
  });
  const queue = data?.data || [];
  const total = data?.total ?? queue.length;

  const applyDecision = async (callId, decision) => {
    setDecidingId(callId);
    try {
      await apiPost(`/bots/${bot._id}/training/${callId}/decision`, { decision });
      const labels = {
        approved:     'Approved — will enter corpus on next persona run',
        rejected:     'Rejected — call excluded from corpus',
        reference:    'Promoted to Reference call',
        'anti-corpus': 'Added to Anti-Corpus',
      };
      toast.success(labels[decision] || 'Updated');
      refetch();
      refetchBot();
      qc.invalidateQueries({ queryKey: ['bot-training-calls', bot._id] });
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed');
    } finally {
      setDecidingId(null);
    }
  };

  if (isLoading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-civix-purple animate-spin" /></div>;
  }

  return (
    <div className="space-y-4">
      {/* Header info */}
      <div className="panel p-4 flex items-center gap-3 border border-yellow-400/20 bg-yellow-400/5">
        <Eye className="w-5 h-5 text-yellow-400 shrink-0" />
        <div className="flex-1">
          <div className="text-sm font-medium text-white">Shadow Review Queue</div>
          <div className="text-xs text-white/50">
            {total} call{total !== 1 ? 's' : ''} awaiting review — scored 40–70 by AI judge. Approve, reject, promote to reference, or mark as anti-corpus.
          </div>
        </div>
      </div>

      {queue.length === 0 ? (
        <div className="text-center py-16">
          <CheckCheck className="w-10 h-10 text-green-400/40 mx-auto mb-3" />
          <div className="text-white/60 font-medium">Queue is clear</div>
          <div className="text-white/30 text-sm mt-1">New shadow calls will appear here when the AI judge flags them for review</div>
        </div>
      ) : (
        <div className="space-y-3">
          {queue.map((tc) => {
            const jb = tc.judgeBreakdown || {};
            const isExpanded = expandedId === tc._id;
            const isDeciding = decidingId === tc._id;
            return (
              <div key={tc._id} className="panel p-4">
                {/* Header row */}
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-sm font-medium text-white">
                        {tc.originalFilename || `Shadow call ${tc._id.slice(-6)}`}
                      </span>
                      <JudgeBadge decision={tc.decision} />
                      {jb.overallScore != null && (
                        <span className="text-xs text-white/40">Score: {jb.overallScore}/100</span>
                      )}
                    </div>
                    <div className="text-xs text-white/30 flex gap-3 flex-wrap">
                      <span>{tc.durationSeconds > 0 ? `${Math.round(tc.durationSeconds / 60)}m` : '—'}</span>
                      {tc.callOutcome && <span>Outcome: {tc.callOutcome}</span>}
                      <span>{new Date(tc.createdAt).toLocaleDateString()}</span>
                    </div>
                    {/* Compliance flags */}
                    {jb.complianceFlags?.length > 0 && (
                      <div className="flex items-center gap-1.5 mt-1.5">
                        <ShieldAlert className="w-3 h-3 text-red-400 shrink-0" />
                        <span className="text-xs text-red-400 font-medium">
                          Compliance: {jb.complianceFlags.join(' · ')}
                        </span>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : tc._id)}
                    className="p-1.5 rounded-lg text-white/30 hover:text-white hover:bg-white/5 transition-colors shrink-0"
                  >
                    <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </button>
                </div>

                {/* Expanded: score breakdown + transcript snippet + action buttons */}
                {isExpanded && (
                  <div className="mt-4 space-y-4 border-t border-white/5 pt-4">
                    {/* Score breakdown */}
                    {jb.overallScore != null && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {[
                          { label: 'Overall',    v: jb.overallScore       },
                          { label: 'Script',     v: jb.scriptAdherence    },
                          { label: 'Objections', v: jb.objectionQuality   },
                          { label: 'Compliance', v: jb.compliance         },
                          { label: 'Tone',       v: jb.toneRapport        },
                          { label: 'Reference',  v: jb.referenceAlignment },
                        ].map(({ label, v }) => (
                          <div key={label} className="bg-white/3 rounded-lg px-3 py-2">
                            <div className="text-xs text-white/40">{label}</div>
                            <div className={`text-sm font-bold ${
                              v == null ? 'text-white/20' :
                              v >= 70   ? 'text-green-400' :
                              v >= 40   ? 'text-yellow-400' : 'text-red-400'
                            }`}>
                              {v != null ? `${v}/100` : '—'}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Reasons */}
                    {jb.reasons?.length > 0 && (
                      <div>
                        <div className="text-xs text-white/40 mb-1.5">Judge Notes</div>
                        <ul className="space-y-1">
                          {jb.reasons.map((r, i) => (
                            <li key={i} className="text-xs text-white/60 flex gap-1.5">
                              <span className="text-white/20 shrink-0">•</span>{r}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Transcript snippet */}
                    {tc.transcript?.length > 0 && (
                      <div>
                        <div className="text-xs text-white/40 mb-1.5">Transcript (first 8 lines)</div>
                        <div className="bg-white/3 rounded-lg p-3 max-h-40 overflow-y-auto space-y-1">
                          {tc.transcript.slice(0, 8).map((seg, i) => (
                            <div key={i} className="text-xs">
                              <span className={`font-medium ${seg.speaker === 'agent' ? 'text-civix-cyan' : 'text-white/40'}`}>
                                {(seg.speaker || 'agent').toUpperCase()}:
                              </span>{' '}
                              <span className="text-white/60">{seg.text}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Action buttons */}
                    <div className="flex flex-wrap gap-2 pt-1">
                      <button
                        onClick={() => applyDecision(tc._id, 'approved')}
                        disabled={isDeciding}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-400/10 text-green-400 hover:bg-green-400/20 text-xs font-medium transition-colors"
                      >
                        {isDeciding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ThumbsUp className="w-3.5 h-3.5" />}
                        Approve
                      </button>
                      <button
                        onClick={() => applyDecision(tc._id, 'reference')}
                        disabled={isDeciding}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-yellow-400/10 text-yellow-400 hover:bg-yellow-400/20 text-xs font-medium transition-colors"
                      >
                        <Star className="w-3.5 h-3.5" />
                        Promote to Reference
                      </button>
                      <button
                        onClick={() => applyDecision(tc._id, 'rejected')}
                        disabled={isDeciding}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 text-white/40 hover:bg-red-400/10 hover:text-red-400 text-xs font-medium transition-colors"
                      >
                        <ThumbsDown className="w-3.5 h-3.5" />
                        Reject
                      </button>
                      <button
                        onClick={() => applyDecision(tc._id, 'anti-corpus')}
                        disabled={isDeciding}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 text-white/40 hover:bg-red-400/10 hover:text-red-400 text-xs font-medium transition-colors"
                        title="Mark as Anti-Corpus — patterns from this call will be injected into persona derivation as things to AVOID"
                      >
                        <ShieldAlert className="w-3.5 h-3.5" />
                        Anti-Corpus
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
const TABS_BASE = [
  { id: 'overview',       label: 'Overview',       icon: Bot       },
  { id: 'training',       label: 'Training',       icon: UploadCloud },
  { id: 'persona',        label: 'Persona',        icon: Brain     },
  { id: 'test',           label: 'Test',           icon: Zap       },
  { id: 'shadow-review',  label: 'Shadow Review',  icon: Eye       },
];

export default function BotDetail() {
  const { id }  = useParams();
  const nav     = useNavigate();
  const [tab, setTab] = useState('overview');
  const [showShare, setShowShare] = useState(false);
  const [clearingDrift, setClearingDrift] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['bot', id],
    queryFn:  () => apiGet(`/bots/${id}`),
    staleTime: 5000,
    refetchInterval: (data) => {
      const s = data?.data?.status;
      return s === 'training' ? 4000 : 15000;
    },
  });

  const bot = data?.data || {};

  // Phase 3: ownership awareness. _isOwner is injected by the API.
  const isOwner = bot._isOwner !== false;

  // Phase 4: review queue count for badge on tab
  const { data: reviewQueueData } = useQuery({
    queryKey: ['bot-review-queue-count', id],
    queryFn:  () => apiGet(`/bots/${id}/review-queue`),
    enabled:  isOwner && !!bot._id,
    staleTime: 30000,
    refetchInterval: 60000,
  });
  const reviewQueueCount = reviewQueueData?.total ?? 0;

  // Phase 4: tabs — show Shadow Review only to owners
  const TABS = isOwner ? TABS_BASE : TABS_BASE.filter((t) => t.id !== 'shadow-review');

  // Phase 4: clear drift alert
  const clearDrift = async () => {
    setClearingDrift(true);
    try {
      await apiPost(`/bots/${bot._id}/drift/clear`, {});
      toast.success('Drift alert cleared — auto-ingest resumed');
      refetch();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed');
    } finally {
      setClearingDrift(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-civix-dark flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-civix-purple animate-spin" />
      </div>
    );
  }

  if (!bot._id) {
    return (
      <div className="min-h-screen bg-civix-dark flex items-center justify-center">
        <div className="text-white/40">Bot not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-civix-dark">
      <TopBar
        title={bot.name || 'Bot Detail'}
        subtitle={`v${bot.version || 1} · ${bot.type === 'voice' ? 'Voice Bot' : 'Text Bot'}`}
        left={
          <button onClick={() => nav('/bots')} className="p-2 rounded-lg hover:bg-white/5 text-white/40 hover:text-white mr-2">
            <ArrowLeft className="w-5 h-5" />
          </button>
        }
      />

      <div className="p-6 max-w-4xl mx-auto">

        {/* Phase 4: Drift alert banner */}
        {bot.driftAlert?.detected && isOwner && (
          <div className="mb-5 bg-orange-400/10 border border-orange-400/30 rounded-xl px-4 py-3 flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-orange-400 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-orange-400 mb-0.5">Persona Drift Detected</div>
              <div className="text-xs text-white/60">
                {bot.driftAlert.summary || 'The bot\'s persona has shifted significantly from the previous version. Auto-ingest is paused.'}
                {bot.driftAlert.detectedAt && (
                  <span className="text-white/30 ml-2">· {new Date(bot.driftAlert.detectedAt).toLocaleDateString()}</span>
                )}
              </div>
            </div>
            <button
              onClick={clearDrift}
              disabled={clearingDrift}
              className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-orange-400/20 text-orange-400 hover:bg-orange-400/30 text-xs font-medium transition-colors"
            >
              {clearingDrift ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCheck className="w-3.5 h-3.5" />}
              Acknowledge & Resume
            </button>
          </div>
        )}

        {/* Status banner + ownership indicators */}
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <StatusBadge status={bot.status} />
          {bot.trainingCorpusSize > 0 && (
            <span className="text-xs text-white/30">
              {bot.trainingCorpusSize} call{bot.trainingCorpusSize !== 1 ? 's' : ''} · {Math.round(bot.trainingCorpusMinutes || 0)} min trained
            </span>
          )}
          {bot.status === 'training' && (
            <span className="text-xs text-yellow-400 flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" /> Deriving persona…
            </span>
          )}
          {/* Phase 4: reference calls + anti-corpus counts */}
          {isOwner && bot.referenceCalls?.length > 0 && (
            <span className="text-xs text-yellow-400/70 flex items-center gap-1">
              <Star className="w-3 h-3" /> {bot.referenceCalls.length} ref
            </span>
          )}
          {/* Phase 3: Shared-by badge for non-owners */}
          {!isOwner && (
            <span className="px-2 py-0.5 rounded-full bg-civix-cyan/10 border border-civix-cyan/20 text-civix-cyan text-xs font-medium flex items-center gap-1.5">
              <UserCheck className="w-3 h-3" />
              Shared with you ({bot._shareScope === 'clone' ? 'use + clone' : 'use only'})
            </span>
          )}
          {/* Phase 3: Share button — owner only */}
          {isOwner && (
            <button
              onClick={() => setShowShare(true)}
              className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/60 hover:text-white text-xs font-medium transition-colors"
            >
              <Share2 className="w-3.5 h-3.5" />
              Share
            </button>
          )}
        </div>

        {/* Non-owner read-only notice */}
        {!isOwner && (
          <div className="mb-5 bg-white/3 border border-white/10 rounded-xl px-4 py-3 flex items-center gap-2 text-xs text-white/50">
            <Lock className="w-3.5 h-3.5 shrink-0" />
            You have read-only access. Edit, training, and persona derivation are locked to the bot owner.
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white/5 rounded-xl p-1 overflow-x-auto">
          {TABS.map(({ id: tid, label, icon: Icon }) => (
            <button
              key={tid}
              onClick={() => setTab(tid)}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${ 
                tab === tid
                  ? 'bg-civix-purple text-white'
                  : 'text-white/40 hover:text-white hover:bg-white/5'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span className="hidden sm:inline">{label}</span>
              {!isOwner && (tid === 'training') && (
                <Lock className="w-3 h-3 text-white/20" />
              )}
              {/* Phase 4: review queue badge */}
              {tid === 'shadow-review' && reviewQueueCount > 0 && (
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-yellow-400 text-black text-[9px] font-bold shrink-0">
                  {reviewQueueCount > 9 ? '9+' : reviewQueueCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {tab === 'overview'       && <OverviewTab      bot={bot} refetch={refetch} isOwner={isOwner} />}
        {tab === 'training'       && (
          isOwner
            ? <TrainingTab bot={bot} refetch={refetch} />
            : (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <Lock className="w-10 h-10 text-white/20 mb-4" />
                <div className="text-white font-medium mb-1">Training locked</div>
                <div className="text-white/40 text-sm max-w-xs">
                  Only the bot owner can upload training calls or re-derive the persona.
                </div>
              </div>
            )
        )}
        {tab === 'persona'        && <PersonaTab       bot={bot} refetch={refetch} />}
        {tab === 'test'           && <TestTab          bot={bot} />}
        {tab === 'shadow-review'  && isOwner && <ShadowReviewTab bot={bot} refetch={refetch} />}
      </div>

      {/* Share modal */}
      {showShare && isOwner && (
        <ShareModal bot={bot} onClose={() => setShowShare(false)} />
      )}
    </div>
  );
}
